import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  Calculator,
  ChevronRight,
  Globe,
  Lightbulb,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/features/auth/useAuth'
import { useCategories, useMaterials } from '@/features/materials/useMaterials'
import { CategoryIndex } from '@/lib/category-ref'
import { QuestionEditDialog } from '@/features/rules/QuestionEditDialog'
import { SimulationDialog } from '@/features/rules/SimulationDialog'
import {
  EstateTypeDialog,
  GroupDialog,
  HintDialog,
  SectionTypeDialog,
} from '@/features/rules/RuleNodeDialogs'
import {
  deleteEstateType,
  deleteGroup,
  deleteHint,
  deleteQuestion,
  deleteSectionType,
} from '@/features/rules/rules-api'
import { useRules } from '@/features/rules/useRules'
import { describeWriteError } from '@/features/users/permissions'
import { DeepLinkMissing } from '@/components/DeepLinkMissing'
import { useDeepLink } from '@/lib/use-deep-link'
import { ANSWER_TYPE_LABELS } from '@/lib/constants'
import type { ConfigQuestion, EstateType, QuestionGroup, QuestionHint, SectionType } from '@/lib/types'
import { cn } from '@/lib/utils'

type Tab = 'tree' | 'groups'

/**
 * Stage 5: the calculation-rules tree.
 *
 * Two tabs, because the model has two entry points: the estate/section tree (questions that belong to
 * a section type) and the reusable groups (questions that belong to a group, plus the group's hints).
 * A question editor is shared by both — the only difference is which owner id it writes.
 */
export function RulesPage() {
  const { user } = useAuth()
  const rules = useRules()
  const { categories } = useCategories()
  const categoryIndex = useMemo(() => new CategoryIndex(categories), [categories])
  const { materials } = useMaterials(user?.uid)

  const [tab, setTab] = useState<Tab>('tree')
  const [estateId, setEstateId] = useState<string | null>(null)
  const [sectionId, setSectionId] = useState<string | null>(null)
  const [groupId, setGroupId] = useState<string | null>(null)
  const [writeError, setWriteError] = useState<string | null>(null)

  // Dialog state; `undefined` = closed, `null` = create, object = edit.
  const [estateDialog, setEstateDialog] = useState<EstateType | null | undefined>(undefined)
  const [sectionDialog, setSectionDialog] = useState<SectionType | null | undefined>(undefined)
  const [groupDialog, setGroupDialog] = useState<QuestionGroup | null | undefined>(undefined)
  const [hintDialog, setHintDialog] = useState<QuestionHint | null | undefined>(undefined)
  const [questionDialog, setQuestionDialog] = useState<ConfigQuestion | null | undefined>(undefined)
  // Section type whose calculation is being simulated (answers live only inside the dialog).
  const [simulateId, setSimulateId] = useState<string | null>(null)
  const simulateSection = rules.sectionTypes.find((item) => item.id === simulateId) ?? null
  // Live config: editing rules in another tab updates the simulation without losing its answers.
  const simulationConfig = useMemo(
    () =>
      simulateSection
        ? {
            sectionType: simulateSection,
            questions: rules.questions,
            groups: rules.groups,
            options: rules.options,
            effects: rules.effects,
            hints: rules.hints,
            materials,
            categories,
          }
        : null,
    [
      simulateSection,
      rules.questions,
      rules.groups,
      rules.options,
      rules.effects,
      rules.hints,
      materials,
      categories,
    ],
  )
  const [confirm, setConfirm] = useState<{ title: string; body: string; run: () => Promise<void> } | null>(
    null,
  )

  // Links from "Несоответствия": `/rules?question=|hint=|section=|group=|estate=<id>` select the
  // item's place in the tree and open its editor.
  const rulesReady = !rules.isLoading
  const selectSection = (section: SectionType) => {
    setTab('tree')
    setEstateId(section.estateTypeId)
    setSectionId(section.id)
  }
  const selectGroup = (id: string) => {
    setTab('groups')
    setGroupId(id)
  }
  const questionLink = useDeepLink('question', rules.questions, rulesReady, (question) => {
    if (question.groupId) {
      selectGroup(question.groupId)
    } else {
      const section = rules.sectionTypes.find((item) => item.id === question.sectionTypeId)
      if (!section) return
      selectSection(section)
    }
    setQuestionDialog(question)
  })
  const hintLink = useDeepLink('hint', rules.hints, rulesReady, (hint) => {
    selectGroup(hint.groupId)
    setHintDialog(hint)
  })
  const sectionLink = useDeepLink('section', rules.sectionTypes, rulesReady, (section) => {
    selectSection(section)
    setSectionDialog(section)
  })
  const groupLink = useDeepLink('group', rules.groups, rulesReady, (group) => selectGroup(group.id))
  const estateLink = useDeepLink('estate', rules.estateTypes, rulesReady, (estate) => {
    setTab('tree')
    setEstateId(estate.id)
    setSectionId(null)
  })
  const missingLink = [
    { link: questionLink, what: 'Вопрос' },
    { link: hintLink, what: 'Подсказка' },
    { link: sectionLink, what: 'Тип раздела' },
    { link: groupLink, what: 'Группа' },
    { link: estateLink, what: 'Тип объекта' },
  ].find((item) => item.link.missingId !== null)

  const selectedEstate = rules.estateTypes.find((item) => item.id === estateId) ?? null
  const sections = useMemo(
    () => rules.sectionTypes.filter((item) => item.estateTypeId === (estateId ?? '')),
    [rules.sectionTypes, estateId],
  )
  const selectedSection = sections.find((item) => item.id === sectionId) ?? null
  const selectedGroup = rules.groups.find((item) => item.id === groupId) ?? null

  const ownerQuestions = useMemo(() => {
    if (tab === 'tree') {
      return selectedSection
        ? rules.questions.filter((item) => item.sectionTypeId === selectedSection.id)
        : []
    }
    return selectedGroup ? rules.questions.filter((item) => item.groupId === selectedGroup.id) : []
  }, [tab, rules.questions, selectedSection, selectedGroup])

  const groupHints = useMemo(
    () => (selectedGroup ? rules.hints.filter((item) => item.groupId === selectedGroup.id) : []),
    [rules.hints, selectedGroup],
  )

  // Informational: the groups whose questions a room of the selected section type also gets — every
  // global group plus the selected ones, in the app's order (`SectionQuestionResolver`: group
  // orderIndex, then id), each with its own questions by orderIndex. Edited in "Группы вопросов".
  const sectionGroupBlocks = useMemo(() => {
    if (tab !== 'tree' || !selectedSection) return []
    const selected = new Set(selectedSection.groupIds)
    return rules.groups
      .filter((group) => group.isGlobal || selected.has(group.id))
      .sort((left, right) =>
        left.orderIndex !== right.orderIndex
          ? left.orderIndex - right.orderIndex
          : left.id < right.id
            ? -1
            : left.id > right.id
              ? 1
              : 0,
      )
      .map((group) => ({
        group,
        questions: rules.questions
          .filter((question) => question.groupId === group.id)
          .sort((left, right) => left.orderIndex - right.orderIndex),
      }))
  }, [tab, selectedSection, rules.groups, rules.questions])

  const questionSubtitle = (item: ConfigQuestion) =>
    `${ANSWER_TYPE_LABELS[item.answerType]}${
      item.answerType === 'FACT' ? ' · не виден пользователю' : ` · {${item.factKey || '—'}}`
    }${
      item.unit ? ` · ${item.unit}` : ''
    }${
      item.materialCategory || item.materialCategoryId
        ? ` · категория: ${categoryIndex.displayName(item.materialCategoryId, item.materialCategory) || '(удалена)'}`
        : ''
    } · вариантов: ${
      rules.options.filter((option) => option.questionId === item.id).length
    } · эффектов: ${
      rules.effects.filter((effect) => effect.questionId === item.id).length
    }${item.displayCondition ? ' · есть условие' : ''}`

  const owner = useMemo(
    () =>
      tab === 'tree'
        ? selectedSection
          ? ({ kind: 'section', id: selectedSection.id } as const)
          : null
        : selectedGroup
          ? ({ kind: 'group', id: selectedGroup.id } as const)
          : null,
    [tab, selectedSection, selectedGroup],
  )

  const run = async (action: () => Promise<void>) => {
    setWriteError(null)
    try {
      await action()
      setConfirm(null)
    } catch (caught) {
      setWriteError(describeWriteError(caught))
    }
  }

  const countsFor = (section: SectionType) =>
    rules.questions.filter((item) => item.sectionTypeId === section.id).length

  const groupQuestionCount = (group: QuestionGroup) =>
    rules.questions.filter((item) => item.groupId === group.id).length

  if (rules.isLoading) {
    return (
      <div className="mx-auto flex max-w-6xl flex-col gap-3">
        {[0, 1, 2, 3, 4].map((row) => (
          <Skeleton key={row} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold">Правила расчёта</h1>
        <p className="text-muted-foreground text-sm">
          Типы объектов → типы разделов → вопросы → варианты и эффекты на материалы. Плюс
          переиспользуемые группы вопросов и их подсказки.
        </p>
      </div>

      <div className="bg-muted flex w-fit rounded-lg p-1">
        {(
          [
            ['tree', 'Объекты и разделы'],
            ['groups', 'Группы вопросов'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            aria-pressed={tab === value}
            className={cn(
              'cursor-pointer rounded-md px-3 py-1.5 text-sm transition-colors',
              tab === value
                ? 'bg-background text-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {rules.error ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Конфигурация не загружена</AlertTitle>
          <AlertDescription>{rules.error}</AlertDescription>
        </Alert>
      ) : null}

      {missingLink ? (
        <DeepLinkMissing
          missingId={missingLink.link.missingId}
          what={missingLink.what}
          onDismiss={missingLink.link.dismiss}
        />
      ) : null}

      {writeError ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Действие не выполнено</AlertTitle>
          <AlertDescription>{writeError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        {tab === 'tree' ? (
          <>
            <ListPanel
              title="Типы объектов"
              onAdd={() => setEstateDialog(null)}
              isEmpty={rules.estateTypes.length === 0}
              emptyText="Типов объектов пока нет."
            >
              {rules.estateTypes.map((item) => (
                <Row
                  key={item.id}
                  title={item.name}
                  subtitle={`разделов: ${
                    rules.sectionTypes.filter((section) => section.estateTypeId === item.id).length
                  }`}
                  isSelected={item.id === estateId}
                  onSelect={() => {
                    setEstateId(item.id)
                    setSectionId(null)
                  }}
                  onEdit={() => setEstateDialog(item)}
                  onDelete={() =>
                    setConfirm({
                      title: 'Удалить тип объекта?',
                      body: `«${item.name}» будет удалён вместе со всеми его типами разделов, их вопросами, вариантами и эффектами. Действие необратимо.`,
                      run: () => deleteEstateType(item.id),
                    })
                  }
                />
              ))}
            </ListPanel>

            <ListPanel
              title="Типы разделов"
              onAdd={selectedEstate ? () => setSectionDialog(null) : undefined}
              isEmpty={sections.length === 0}
              emptyText={
                selectedEstate ? 'Разделов пока нет.' : 'Выберите тип объекта слева.'
              }
            >
              {sections.map((item) => (
                <Row
                  key={item.id}
                  title={item.name}
                  subtitle={`вопросов: ${countsFor(item)}${
                    item.groupIds.length > 0 ? ` · групп: ${item.groupIds.length}` : ''
                  }`}
                  isSelected={item.id === sectionId}
                  onSelect={() => setSectionId(item.id)}
                  onSimulate={() => setSimulateId(item.id)}
                  onEdit={() => setSectionDialog(item)}
                  onDelete={() =>
                    setConfirm({
                      title: 'Удалить тип раздела?',
                      body: `«${item.name}» будет удалён вместе со своими вопросами, их вариантами и эффектами.`,
                      run: () => deleteSectionType(item.id),
                    })
                  }
                />
              ))}
            </ListPanel>
          </>
        ) : (
          <>
            <ListPanel
              title="Группы вопросов"
              onAdd={() => setGroupDialog(null)}
              isEmpty={rules.groups.length === 0}
              emptyText="Групп пока нет."
            >
              {rules.groups.map((item) => (
                <Row
                  key={item.id}
                  title={item.name}
                  badge={
                    item.isGlobal ? (
                      <Badge variant="outline" className="gap-1">
                        <Globe className="size-3" />
                        глобальная
                      </Badge>
                    ) : null
                  }
                  subtitle={`вопросов: ${groupQuestionCount(item)} · подсказок: ${
                    rules.hints.filter((hint) => hint.groupId === item.id).length
                  }`}
                  isSelected={item.id === groupId}
                  onSelect={() => setGroupId(item.id)}
                  onEdit={() => setGroupDialog(item)}
                  onDelete={() =>
                    setConfirm({
                      title: 'Удалить группу?',
                      body: `«${item.name}» будет удалена вместе со своими вопросами (и их вариантами и эффектами) и подсказками.`,
                      run: () => deleteGroup(item.id),
                    })
                  }
                />
              ))}
            </ListPanel>

            <ListPanel
              title="Подсказки группы"
              onAdd={selectedGroup ? () => setHintDialog(null) : undefined}
              isEmpty={groupHints.length === 0}
              emptyText={selectedGroup ? 'Подсказок пока нет.' : 'Выберите группу слева.'}
            >
              {groupHints.map((item) => (
                <Row
                  key={item.id}
                  title={item.text || '(без текста)'}
                  badge={<Lightbulb className="text-muted-foreground size-3.5" />}
                  subtitle={item.condition || 'условие не задано — не показывается'}
                  mono
                  onEdit={() => setHintDialog(item)}
                  onDelete={() =>
                    setConfirm({
                      title: 'Удалить подсказку?',
                      body: 'Подсказка будет удалена. На расчёт это не влияет.',
                      run: () => deleteHint(item.id),
                    })
                  }
                />
              ))}
            </ListPanel>
          </>
        )}

        <ListPanel
          title="Вопросы"
          onAdd={owner ? () => setQuestionDialog(null) : undefined}
          isEmpty={tab === 'tree' ? !selectedSection : ownerQuestions.length === 0}
          emptyText={
            owner
              ? 'Вопросов пока нет.'
              : tab === 'tree'
                ? 'Выберите тип раздела.'
                : 'Выберите группу.'
          }
        >
          {tab === 'tree' ? (
            <>
              <SectionHeading
                title="Вопросы от групп"
                count={sectionGroupBlocks.reduce((sum, block) => sum + block.questions.length, 0)}
                note="Только просмотр: правятся в «Группах вопросов»"
              />
              {sectionGroupBlocks.length === 0 ? (
                <p className="text-muted-foreground px-2 pb-2 text-xs">
                  Раздел не подключает групп, глобальных групп нет.
                </p>
              ) : (
                sectionGroupBlocks.map((block) => (
                  <div key={block.group.id} className="flex flex-col gap-0.5 pb-1">
                    <div className="flex items-center gap-2 px-2 pt-1">
                      <span className="truncate text-xs font-medium">{block.group.name}</span>
                      {block.group.isGlobal ? (
                        <Badge variant="outline" className="gap-1">
                          <Globe className="size-3" />
                          глобальная
                        </Badge>
                      ) : null}
                    </div>
                    {block.questions.length === 0 ? (
                      <p className="text-muted-foreground px-2 text-xs">В группе нет вопросов.</p>
                    ) : (
                      block.questions.map((item) => (
                        <div key={item.id} className="rounded-md px-2 py-1.5">
                          <span className="block truncate text-sm">{item.prompt || '(без текста)'}</span>
                          <span className="text-muted-foreground block truncate text-xs">
                            {questionSubtitle(item)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                ))
              )}
              <SectionHeading title="Индивидуальные вопросы" count={ownerQuestions.length} />
              {ownerQuestions.length === 0 ? (
                <p className="text-muted-foreground px-2 pb-2 text-xs">Своих вопросов пока нет.</p>
              ) : null}
            </>
          ) : null}
          {ownerQuestions.map((item) => (
            <Row
              key={item.id}
              title={item.prompt || '(без текста)'}
              subtitle={questionSubtitle(item)}
              onEdit={() => setQuestionDialog(item)}
              onDelete={() =>
                setConfirm({
                  title: 'Удалить вопрос?',
                  body: 'Вопрос будет удалён вместе со своими вариантами и эффектами. Ответы, уже сохранённые в проектах, останутся, но перестанут учитываться.',
                  run: () => deleteQuestion(item.id),
                })
              }
            />
          ))}
        </ListPanel>
      </div>

      <p className="text-muted-foreground text-xs">
        Удаление каскадное, как в приложении: тип объекта уносит свои разделы, раздел — свои вопросы,
        вопрос — свои варианты и эффекты. Варианты и эффекты сохраняются вместе с вопросом одной
        атомарной операцией, поэтому «половинчатой» правки логики быть не может. Порядок задаётся полем
        «порядок», а у вариантов — порядком строк.
      </p>

      {estateDialog !== undefined ? (
        <EstateTypeDialog
          estateType={estateDialog}
          nextOrder={rules.estateTypes.length}
          onClose={() => setEstateDialog(undefined)}
        />
      ) : null}

      {sectionDialog !== undefined && selectedEstate ? (
        <SectionTypeDialog
          sectionType={sectionDialog}
          estateTypeId={selectedEstate.id}
          groups={rules.groups}
          nextOrder={sections.length}
          onClose={() => setSectionDialog(undefined)}
        />
      ) : null}

      {simulationConfig ? (
        <SimulationDialog
          uid={user?.uid}
          estateName={
            rules.estateTypes.find((item) => item.id === simulationConfig.sectionType.estateTypeId)
              ?.name ?? ''
          }
          config={simulationConfig}
          onClose={() => setSimulateId(null)}
        />
      ) : null}

      {groupDialog !== undefined ? (
        <GroupDialog
          group={groupDialog}
          nextOrder={rules.groups.length}
          onClose={() => setGroupDialog(undefined)}
        />
      ) : null}

      {hintDialog !== undefined && selectedGroup ? (
        <HintDialog
          hint={hintDialog}
          groupId={selectedGroup.id}
          nextOrder={groupHints.length}
          allQuestions={rules.questions}
          allOptions={rules.options}
          groups={rules.groups}
          onClose={() => setHintDialog(undefined)}
        />
      ) : null}

      {questionDialog !== undefined && owner ? (
        <QuestionEditDialog
          question={questionDialog}
          owner={owner}
          options={
            questionDialog
              ? rules.options.filter((option) => option.questionId === questionDialog.id)
              : []
          }
          effects={
            questionDialog
              ? rules.effects.filter((effect) => effect.questionId === questionDialog.id)
              : []
          }
          categories={categories}
          materials={materials}
          nextOrder={ownerQuestions.length}
          allQuestions={rules.questions}
          allOptions={rules.options}
          groups={rules.groups}
          sectionTypes={rules.sectionTypes}
          onClose={() => setQuestionDialog(undefined)}
        />
      ) : null}

      {confirm ? (
        <Dialog open onOpenChange={(open) => !open && setConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{confirm.title}</DialogTitle>
              <DialogDescription>{confirm.body}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirm(null)}>
                Отмена
              </Button>
              <Button variant="destructive" onClick={() => void run(confirm.run)}>
                <Trash2 />
                Удалить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  )
}

/** A sub-heading inside a list panel (the two parts of a section type's questions). */
function SectionHeading({ title, count, note }: { title: string; count: number; note?: string }) {
  return (
    <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-2 border-b px-2 pt-1 pb-1">
      <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
        {title} · {count}
      </span>
      {note ? <span className="text-muted-foreground text-[11px]">{note}</span> : null}
    </div>
  )
}

function ListPanel({
  title,
  onAdd,
  isEmpty,
  emptyText,
  children,
}: {
  title: string
  onAdd?: () => void
  isEmpty: boolean
  emptyText: string
  children: React.ReactNode
}) {
  return (
    <Card className="gap-0 py-0">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <span className="text-sm font-medium">{title}</span>
        {onAdd ? (
          <Button variant="ghost" size="sm" onClick={onAdd}>
            <Plus />
            Добавить
          </Button>
        ) : null}
      </div>
      <CardContent className="max-h-[28rem] overflow-y-auto px-2 py-2">
        {isEmpty ? (
          <p className="text-muted-foreground px-2 py-6 text-center text-xs">{emptyText}</p>
        ) : (
          <div className="flex flex-col gap-1">{children}</div>
        )}
      </CardContent>
    </Card>
  )
}

function Row({
  title,
  subtitle,
  badge,
  isSelected,
  mono,
  onSelect,
  onSimulate,
  onEdit,
  onDelete,
}: {
  title: string
  subtitle: string
  badge?: React.ReactNode
  isSelected?: boolean
  mono?: boolean
  onSelect?: () => void
  /** Section types only: opens the calculation simulation. */
  onSimulate?: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div
      className={cn(
        'group flex items-center gap-1 rounded-md px-2 py-1.5',
        isSelected ? 'bg-primary/10' : 'hover:bg-accent',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        className={cn('min-w-0 flex-1 text-left', onSelect ? 'cursor-pointer' : 'cursor-default')}
      >
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{title}</span>
          {badge}
        </span>
        <span
          className={cn(
            'text-muted-foreground block truncate text-xs',
            mono ? 'font-mono' : undefined,
          )}
        >
          {subtitle}
        </span>
      </button>
      {onSimulate ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={onSimulate}
          aria-label={`Симулировать калькуляцию: ${title}`}
          title="Симулировать калькуляцию"
        >
          <Calculator />
        </Button>
      ) : null}
      <Button variant="ghost" size="icon" onClick={onEdit} aria-label={`Изменить: ${title}`}>
        <Pencil />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="text-destructive hover:text-destructive"
        onClick={onDelete}
        aria-label={`Удалить: ${title}`}
      >
        <Trash2 />
      </Button>
      {onSelect ? <ChevronRight className="text-muted-foreground size-4 shrink-0" /> : null}
    </div>
  )
}
